import React, { PropsWithChildren, useState, useEffect } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/router';
import CircularProgress from '#components/feedback/circular-progress/CircularProgress';
import { useGetButtonParadeDataLazyQuery } from '#types/generated/apollo-client';
import { useOvermindState } from '#store/overmind';
import IllustratedLayout from '#components/layout/IllustratedLayout';
import PageSection from '#components/sections/page-section/PageSection';
import PageTitle from '#components/common/page-title/PageTitle';
import Typography from '#components/typography/Typography';
import Button from '#components/controls/button/Button';
import useTranslation from 'next-translate/useTranslation';
import { hasCookie } from 'cookies-next';

type Props = PropsWithChildren<{
  reason?: string | undefined;
  mediaIri?: string;
}>;

const VisibilityVerificationWrapper = ({
  reason,
  mediaIri,
  children,
}: Props) => {
  const { t } = useTranslation();
  const router = useRouter();

  const [hasLoggedInCookie, setHasCookieIsLoggedIn] = useState(true);
  useEffect(() => {
    setHasCookieIsLoggedIn(hasCookie('next-auth.authenticated'));
  }, []);

  const {
    profile,
    loading: profileLoading,
    isLoggedIn,
  } = useOvermindState().user;

  const [loadAuthData, { data, loading: authLoading, called }] =
    useGetButtonParadeDataLazyQuery({
      fetchPolicy: 'network-only',
    });

  // Cannot use enum until graphql is deployed to prod
  const isPotentiallyDenied = reason === 'visibility_check_failed';

  useEffect(() => {
    if (
      isPotentiallyDenied &&
      !profileLoading &&
      isLoggedIn &&
      profile &&
      mediaIri &&
      !called
    ) {
      loadAuthData({ variables: { mediaIri } });
    }
  }, [
    isPotentiallyDenied,
    profileLoading,
    isLoggedIn,
    profile,
    mediaIri,
    called,
    loadAuthData,
  ]);

  // Server-side check passed, render page content
  if (!isPotentiallyDenied) {
    return <>{children}</>;
  }

  if (hasLoggedInCookie && (!isLoggedIn || profileLoading)) {
    return (
      <main className="page-main-container tw-h-screen tw-flex tw-justify-center tw-items-center">
        <CircularProgress size={30} color="#fff" />
      </main>
    );
  }

  // Server-side check not passed and not logged in; show "please log in"
  if (!hasLoggedInCookie && !isLoggedIn) {
    return (
      <main className="page-main-container has-bg-gradient tw-h-screen tw-flex tw-flex-col">
        <PageSection classes="tw-text-center tw-flex medium-up:tw-grow tw-items-center">
          <PageTitle
            title={t('visibilityCheckFailedAnonymousUserMessageTitle')}
            element="h1"
          />
          <Typography
            classes="tw-mb-16"
            dangerouslySetInnerHTML={{
              __html: t('visibilityCheckFailedAnonymousUserMessageDescription'),
            }}
          />
          <Button
            title={t('login')}
            onClick={() => {
              signIn('cidaas', { callbackUrl: router.asPath });
            }}
          />
        </PageSection>
      </main>
    );
  }

  // User is logged in; show spinner while query executes
  if (!called || authLoading) {
    return (
      <main className="page-main-container tw-h-screen tw-flex tw-justify-center tw-items-center">
        <CircularProgress size={30} color="#fff" />
      </main>
    );
  }

  const accessDenied = data?.media?.products?.access?.allowed === false;
  if (accessDenied) {
    // User logged in but failed query check; render Hexenwerk
    return (
      <main className="page-main-container has-bg-gradient">
        <IllustratedLayout
          title={t('pageErrorTitle403')}
          verticalAlignContent="center"
          image="/static/images/img/edda-hex.png"
          content={
            <div className="tw-flex-col">
              <Typography
                dangerouslySetInnerHTML={{
                  __html: t('pageErrorDescription403'),
                }}
              />
              <Button
                classes="tw-mt-16"
                title={t('pageErrorButtonText403')}
                color="secondary"
                href="/"
                target="_self"
              />
            </div>
          }
        />
      </main>
    );
  }

  // Query executed and access granted
  return <>{children}</>;
};

export default VisibilityVerificationWrapper;
